// src/Api/ServiceProvider.js

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

//dynamic API URL based on environment
const API_URL = `${window.location.origin}/api/`;

//PROD static API URL
// const API_URL = `https://pdi.ngtcs.com/api/`;

//DEV static API URL
// const API_URL = "http://10.10.0.101:3000/api/";
 
export const ServiceProvider = {
    auth: {
        login: `${API_URL}auth/login`,
        new: `${API_URL}auth/generatetoken`,
        logout: `${API_URL}auth/logout`,
        selectBranch: `${API_URL}auth/selectBranch`,
    },

    assert: {
        get: `${API_URL}`,
        downloadPdiExcel: `${API_URL}images/excel_uploads/`
    },

    dashboard: {
        base: `${API_URL}pdi/report`,
        logout: `${API_URL}auth/logout`,
        selectBranch: `${API_URL}auth/selectBranch`,
    },

    docMaster: {
        getAll: `${API_URL}pdi/docmaster/getall`,
        create: `${API_URL}pdi/docmaster/create`,
        createPdi: `${API_URL}pdi/docmaster/uploadpdiExcel`,
        createBuildup: `${API_URL}pdi/docmaster/uploadbuildupExcel`,
        createTest: `${API_URL}pdi/docmaster/uploadtestExcel`,
        update: `${API_URL}pdi/docmaster/update`,
        delete: `${API_URL}pdi/docmaster/delete`,
        replaceItems: `${API_URL}pdi/docmaster/replace-items`,
        assertUpload: `${API_URL}api/pdi/asset/upload`,
        remove: `${API_URL}api/pdi/asset/remove`,
        searchText: `${API_URL}pdi/docmaster/searchmaterial?search_text=`,
        searchByPart: `${API_URL}pdi/docmaster/searchmaterial?pn=`,
        filter: `${API_URL}pdi/docmaster/search-by-tpl`,
        filterPrint: `${API_URL}pdi/docmaster/search`,
    },

    report: {
        getByStatus: `${API_URL}pdi/report/getreportcompleteandpending?`,
        getDrRep: `${API_URL}pdi/report/getDrReport?`,
        getAll: `${API_URL}pdi/report/getall`,
        filter: `${API_URL}pdi/report/search`,
        create: `${API_URL}pdi/report/create`,
        update: `${API_URL}pdi/report/update/`,
        drComplete: `${API_URL}pdi/report/drComplete/`,
        delete: `${API_URL}pdi/report/delete/`,
        getById: `${API_URL}pdi/report/`,
    },

    user: {
        getAll: `${API_URL}pdi/user/all`,
        create: `${API_URL}pdi/user/create`,
        filter: `${API_URL}pdi/user/filter`,
        update: `${API_URL}pdi/user/update/`,
        delete: `${API_URL}pdi/user/delete/`,
        getByUID: `${API_URL}pdi/user/`,
        getByFilter: `${API_URL}pdi/user/`,
        getBranch: `${API_URL}pdi/branch/getallbranch`,
        changePassword: `${API_URL}pdi/user/change-password`,
    },

    location: {
        getAll: `${API_URL}pdi/location/getall`,
        createSingle: `${API_URL}pdi/location/singlecreate`,
        create: `${API_URL}pdi/location/create`,
        bulkCreate: `${API_URL}pdi/location/bulkCreate`,
        getById: `${API_URL}pdi/location/get/`,
        getByPartno: `${API_URL}pdi/location/getbyPartno/`,
        search: `${API_URL}pdi/location/search`,
        update: `${API_URL}pdi/location/update/`,
        delete: `${API_URL}pdi/location/delete/`,
    },
};

export const getDateString = (date = new Date()) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const m = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12 || 12; // convert 0 → 12
    const h = String(hours).padStart(2, '0');

    return `${dd}-${mm}-${yyyy} - ${h}:${m} ${ampm}`;
};

function useAccessTokenWatcher(accessExpireDate, onExpireSoon) {
    const timerRef = useRef(null);

    useEffect(() => {
        if (!accessExpireDate) return;

        // Clear any previous timer if accessExpireDate changes
        if (timerRef.current) clearTimeout(timerRef.current);

        const expireTime = new Date(accessExpireDate).getTime();
        const now = Date.now();

        // 10 minutes before expiry (in milliseconds)
        const tenMinutesBefore = expireTime - 10 * 60 * 1000;

        // Time until we should trigger the callback
        const delay = tenMinutesBefore - now;

        if (delay <= 0) {
            console.log("⚠️ Token already near expiry — triggering immediately");
            onExpireSoon();
            return;
        }

        // Set timer
        timerRef.current = setTimeout(() => {
            console.log("⏰ Access token expires in <10 mins — calling onExpireSoon()");
            onExpireSoon();
        }, delay);

        return () => clearTimeout(timerRef.current);
    }, [accessExpireDate, onExpireSoon]);
}

export default useAccessTokenWatcher;

export function useAppHistory(limit = 5) {
    const location = useLocation();
    const historyRef = useRef([]);

    useEffect(() => {
        const path = location.pathname;
        if (historyRef.current[historyRef.current.length - 1] !== path) {
            historyRef.current.push(path);
            if (historyRef.current.length > limit) historyRef.current.shift();
        }
    }, [location, limit]);

    return historyRef.current;
}